import { css, html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type { Commerce, CommerceStorefrontExperience } from "@teqtank/commerce-sdk";

@customElement("teqtank-storefront-banner")
export class TeqtankStorefrontBanner extends LitElement {
    @property({ attribute: false }) public accessor commerce: Commerce | undefined;
    @property({ type: Boolean }) public accessor dismissible = false;
    @property() public accessor actions = "message profile exit";
    @property({ attribute: "exit-url" }) public accessor exitUrl = "";
    @state() private accessor storefront: CommerceStorefrontExperience | undefined;
    @state() private accessor dismissed = false;
    private unsubscribe: (() => void) | undefined;

    public static override styles = css`
        :host {
            container-type: inline-size;
            display: block;
            font-family: var(--teqtank-font-body, Inter, ui-sans-serif, system-ui, sans-serif);
        }
        .banner {
            display: flex;
            align-items: center;
            gap: 0.85rem;
            padding: 0.7rem clamp(0.8rem, 3cqi, 1.15rem);
            border: 1px solid
                color-mix(
                    in srgb,
                    var(--teqtank-color-accent, #173f35) 18%,
                    var(--teqtank-color-border, #ddd7cc)
                );
            border-radius: 1rem;
            background: var(
                --teqtank-storefront-banner-background,
                linear-gradient(
                    110deg,
                    var(--teqtank-color-surface-soft, #f3efe7),
                    var(--teqtank-color-surface, #fff)
                )
            );
            color: var(--teqtank-storefront-banner-color, var(--teqtank-color-text, #24231f));
            box-shadow: 0 0.55rem 1.6rem rgb(27 38 31 / 7%);
        }
        .avatar {
            display: grid;
            place-items: center;
            width: 2.5rem;
            height: 2.5rem;
            flex: 0 0 auto;
            border-radius: 50%;
            background: var(--teqtank-color-surface-soft, #f3efe7);
            color: var(--teqtank-color-on-accent, #fff);
            background-color: var(--teqtank-color-accent, #173f35);
            font-weight: 800;
            overflow: hidden;
        }
        .avatar img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
        .content {
            min-width: 0;
            flex: 1;
        }
        .eyebrow {
            display: block;
            font-size: 0.72rem;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            color: var(--teqtank-color-text-muted, #68645d);
        }
        strong {
            display: block;
        }
        .role {
            font-size: 0.82rem;
            color: var(--teqtank-color-text-muted, #68645d);
        }
        button,
        a {
            border: 0;
            background: transparent;
            color: var(--teqtank-color-accent, #01676b);
            font: inherit;
            font-weight: 700;
            cursor: pointer;
            border-radius: 999px;
            padding: 0.5rem 0.65rem;
            text-decoration: none;
        }
        button:hover,
        a:hover {
            background: color-mix(in srgb, var(--teqtank-color-accent, #01676b) 8%, transparent);
        }
        button:focus-visible,
        a:focus-visible {
            outline: 3px solid
                color-mix(in srgb, var(--teqtank-color-accent, #01676b) 30%, transparent);
            outline-offset: 2px;
        }
        .actions {
            display: flex;
            align-items: center;
            gap: 0.15rem;
        }
        .exit {
            color: var(--teqtank-color-text-muted, #68645d);
        }
        .dismiss {
            font-size: 1.15rem;
        }
        @media (prefers-reduced-motion: reduce) {
            * {
                scroll-behavior: auto;
            }
        }
        @container (max-width: 620px) {
            .banner {
                flex-wrap: wrap;
            }
            .actions {
                width: 100%;
                padding-left: 3.35rem;
            }
        }
        @container (max-width: 480px) {
            .banner {
                align-items: flex-start;
            }
            .role {
                display: block;
            }
            .actions {
                padding-left: 0;
            }
        }
    `;

    public override connectedCallback(): void {
        super.connectedCallback();
        this.bind();
    }
    protected override updated(changed: Map<PropertyKey, unknown>): void {
        if (changed.has("commerce")) this.bind();
    }
    public override disconnectedCallback(): void {
        this.unsubscribe?.();
        super.disconnectedCallback();
    }

    protected override render() {
        const contact = this.storefront?.contact ?? this.commerce?.storefront.current.contact;
        if (!contact || this.dismissed) return html``;
        const initials = contact.displayName
            .split(/\s+/)
            .slice(0, 2)
            .map((value) => value[0])
            .join("")
            .toUpperCase();
        const actions = new Set(this.actions.split(/[\s,]+/u));
        return html`<section class="banner" part="banner" aria-label="Representative storefront">
            <span class="avatar" part="avatar"
                >${contact.avatar ? html`<img part="avatar-image" src=${contact.avatar.url} alt=${contact.avatar.alternativeText} />` : initials}</span
            ><span class="content" part="content"
                ><span class="eyebrow" part="eyebrow">You're shopping with</span
                ><strong part="name">${contact.displayName}</strong
                ><span class="role" part="role">${contact.role}</span></span
            >
            <span class="actions" part="actions">
                ${actions.has("message") && contact.methods.messageUrl ? html`<a part="action message-action" href=${contact.methods.messageUrl}>Message</a>` : null}
                ${actions.has("profile") ? html`<button class="profile" part="action profile-action" type="button" @click=${this.profile}>View profile</button>` : null}
                ${actions.has("change") ? html`<button class="change" part="action change-action" type="button" @click=${this.change}>Change</button>` : null}
                ${actions.has("exit") ? html`<button class="exit" part="action exit-action" type="button" @click=${this.exit}>Exit site</button>` : null}
                ${
                    this.dismissible
                        ? html`<button
                              class="dismiss"
                              part="dismiss"
                              type="button"
                              aria-label="Dismiss"
                              @click=${() => (this.dismissed = true)}
                          >
                              ×
                          </button>`
                        : null
                }</span
            >
        </section>`;
    }

    private bind(): void {
        this.unsubscribe?.();
        this.unsubscribe = undefined;
        if (!this.commerce) return;
        this.storefront = this.commerce.storefront.current;
        this.unsubscribe = this.commerce.storefront.onChanged(
            (event) => (this.storefront = event.storefront),
        );
    }
    private change(): void {
        this.dispatchEvent(
            new CustomEvent("teqtank-storefront-change-requested", {
                bubbles: true,
                composed: true,
            }),
        );
    }
    private profile(): void {
        this.dispatchEvent(
            new CustomEvent("teqtank-storefront-profile-requested", {
                detail: this.storefront?.contact,
                bubbles: true,
                composed: true,
            }),
        );
    }
    private async exit(): Promise<void> {
        if (!this.commerce) return;
        const storefront = await this.commerce.storefront.clearAttribution();
        this.dispatchEvent(
            new CustomEvent("teqtank-storefront-exited", {
                detail: storefront,
                bubbles: true,
                composed: true,
            }),
        );
        if (this.exitUrl && typeof window !== "undefined") window.location.assign(this.exitUrl);
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "teqtank-storefront-banner": TeqtankStorefrontBanner;
    }
}
